import { ReactiveControllerHost } from 'lit';
import { isEqual } from 'lodash-es';
import { PersistedReduxController } from '../../common/redux/controllers/persisted-redux-controller';
import { RootState, persistor, store, userAPI, selectors, slices, actions } from '../redux/store';
import { TMNotificationType } from '../../notification-system';
import { AuthController } from './auth-controller';

export class NotificationsController extends PersistedReduxController<RootState>  {
  authController: AuthController;

  constructor(host: ReactiveControllerHost, authController: AuthController) {
    super(host, store, persistor);
    this.authController = authController;
  }
  
  get notificationState() {
    return this.state[slices.notification.name];
  }

  get notifications() {
    return selectors.notification.getNotifications(this.notificationState);
  }

  get unreadCount() {
    return selectors.notification.getUnreadCount(this.notificationState);
  }

  get unreadAwardNotifications() {
    return this.notifications.filter((notification) => {
      return notification.type === TMNotificationType.AWARD && !notification.read;
    });
  }

  get unreadSubscriptionNotifications() {
    return this.notifications.filter((notification) => {
      return notification.type === TMNotificationType.SUBSCRIPTION && !notification.read;
    });
  }

  get unreadAwardNotificationsCount() {
    return this.unreadAwardNotifications.length;
  }

  get unreadSubscriptionNotificationsCount() {
    return this.unreadSubscriptionNotifications.length;
  }
  
  override stateChanged(state: RootState, forceUpdate: boolean): void {
    const notificationStateChanged = !isEqual(state[slices.notification.name], this.notificationState);
    if (forceUpdate || notificationStateChanged) {
      this.updateState(state);
      this.host.requestUpdate();
    }
  }

  getNotificationById(id: string) {
    return selectors.notification.getNotificationById(this.notificationState, id);
  }

  markAsRead(id: string) {
    this.dispatch(actions.notification.markAsRead({ id }));
    const authData = this.authController.getAuthRequestData();
    return store.dispatch(userAPI.endpoints.readNotification.initiate({
      ...authData,
      data: {
        notification: id,
      }
    }));
  }

  markNotificationAsDelivered(id: string) {
    store.dispatch(actions.notification.markAsDelivered({ id }));
    const authData = this.authController.getAuthRequestData();
    return store.dispatch(userAPI.endpoints.deliverNotification.initiate({
      ...authData,
      data: {
        notification: id,
      },
    }));
  }

  getPendingNotifications() {
    const authData = this.authController.getAuthRequestData();
    return store.dispatch(userAPI.endpoints.getPendingNotifications.initiate(authData));
  }
}
